if (!customElements.get('localization-form')) {
  customElements.define(
    'localization-form',
    class LocalizationForm extends HTMLElement {
      constructor () {
        super()
        this.mql = window.matchMedia('(min-width: 1024px)')
        this.header = document.querySelector('.header-wrapper')
        this.elements = {
          input: this.querySelector('input[name="locale_code"], input[name="country_code"]'),
          button: this.querySelector('button.localization-form__select'),
          panel: this.querySelector('.disclosure__list-wrapper'),
          search: this.querySelector('input[name="country_filter"]'),
          closeButton: this.querySelector('.country-selector__close-button'),
          resetButton: this.querySelector('.country-filter__reset-button'),
          searchIcon: this.querySelector('.country-filter__search-icon'),
          liveRegion: this.querySelector('#sr-country-search-results')
        }
        // Event delegation - single listeners handle all events for child elements
        this.addEventListener('keyup', this.handleKeyUp.bind(this))
        this.addEventListener('keydown', this.handleKeyDown.bind(this))
        this.addEventListener('focusout', this.closeSelector.bind(this))
        this.addEventListener('click', this.handleClick.bind(this))
        this.addEventListener('focus', this.handleFocus.bind(this), true)
        this.addEventListener('blur', this.handleBlur.bind(this), true)
        if (this.elements.closeButton) {
          this.elements.closeButton.addEventListener('click', this.hidePanel.bind(this))
        }
        if (this.elements.resetButton) {
          this.elements.resetButton.addEventListener('click', this.resetFilter.bind(this))
          this.elements.resetButton.addEventListener('mousedown', (event) => event.preventDefault())
        }

        this.querySelectorAll('a').forEach((item) => item.addEventListener('click', this.onItemClick.bind(this)))
      }

      /**
       * Event delegation handler for keyup events
       * Routes events to appropriate handlers based on the target element
       */
      handleKeyUp (event) {
        if (event.target === this.elements.search) {
          this.filterCountries()
        } else {
          this.onContainerKeyUp(event)
        }
      }

      /**
       * Event delegation handler for keydown events
       * Routes events to appropriate handlers based on the target element
       */
      handleKeyDown (event) {
        if (event.target === this.elements.search) {
          this.onSearchKeyDown(event)
        } else {
          this.onContainerKeyDown(event)
        }
      }

      /**
       * Event delegation handler for click events
       * Routes events to appropriate handlers based on the target element
       */
      handleClick (event) {
        if (event.target === this.elements.button || this.elements.button.contains(event.target)) {
          this.openSelector()
        }
      }

      /**
       * Event delegation handler for focus events (uses capture phase)
       * Routes events to appropriate handlers based on the target element
       */
      handleFocus (event) {
        if (event.target === this.elements.search) {
          this.onSearchFocus()
        }
      }

      /**
       * Event delegation handler for blur events (uses capture phase)
       * Routes events to appropriate handlers based on the target element
       */
      handleBlur (event) {
        if (event.target === this.elements.search) {
          this.onSearchBlur()
        }
      }

      hidePanel () {
        this.elements.button.setAttribute('aria-expanded', 'false')
        this.elements.panel.setAttribute('hidden', true)
        if (this.elements.search) {
          this.elements.search.value = ''
          this.filterCountries()
          this.elements.search.setAttribute('aria-activedescendant', '')
        }
        document.body.classList.remove('overflow-hidden-mobile')
        document.querySelector('.navigation-menu').classList.remove('country-selector-open')
        this.header.preventHide = false
      }

      onContainerKeyDown (event) {
        const focusableItems = Array.from(this.querySelectorAll('a')).filter(
          (item) => !item.parentElement.classList.contains('hidden')
        )
        let focusedItemIndex = focusableItems.findIndex((item) => item === document.activeElement)
        let itemToFocus

        switch (event.code.toUpperCase()) {
          case 'ARROWUP':
            event.preventDefault()
            itemToFocus =
              focusedItemIndex > 0 ? focusableItems[focusedItemIndex - 1] : focusableItems[focusableItems.length - 1]
            itemToFocus.focus()
            break
          case 'ARROWDOWN':
            event.preventDefault()
            itemToFocus =
              focusedItemIndex < focusableItems.length - 1 ? focusableItems[focusedItemIndex + 1] : focusableItems[0]
            itemToFocus.focus()
            break
        }

        if (!this.elements.search) return

        // Use requestAnimationFrame to update aria-activedescendant after DOM updates complete
        requestAnimationFrame(() => {
          focusedItemIndex = focusableItems.findIndex((item) => item === document.activeElement)
          if (focusedItemIndex > -1) {
            this.elements.search.setAttribute('aria-activedescendant', focusableItems[focusedItemIndex].id)
          } else {
            this.elements.search.setAttribute('aria-activedescendant', '')
          }
        })
      }

      onContainerKeyUp (event) {
        event.preventDefault()

        switch (event.code.toUpperCase()) {
          case 'ESCAPE':
            if (this.elements.button.getAttribute('aria-expanded') === 'false') return
            this.hidePanel()
            event.stopPropagation()
            this.elements.button.focus()
            break
          case 'SPACE':
            if (this.elements.button.getAttribute('aria-expanded') === 'true') return
            this.openSelector()
            break
        }
      }

      onItemClick (event) {
        event.preventDefault()
        const form = this.querySelector('form')
        this.elements.input.value = event.currentTarget.dataset.value
        if (form) form.submit()
      }

      openSelector () {
        this.elements.button.focus()
        this.elements.panel.toggleAttribute('hidden')
        this.elements.button.setAttribute(
          'aria-expanded',
          (this.elements.button.getAttribute('aria-expanded') === 'false').toString()
        )
        if (!document.body.classList.contains('overflow-hidden-tablet')) {
          document.body.classList.add('overflow-hidden-mobile')
        }
        if (this.elements.search && this.mql.matches) {
          this.elements.search.focus()
        }
        if (this.hasAttribute('data-prevent-hide')) {
          this.header.preventHide = true
        }
        document.querySelector('.navigation-menu').classList.add('country-selector-open')
      }

      closeSelector (event) {
        if (
          event.target.classList.contains('country-selector__overlay') ||
          !this.contains(event.target) ||
          !this.contains(event.relatedTarget)
        ) {
          this.hidePanel()
        }
      }

      /**
       * Normalizes a string for search comparison by removing accents/diacritics and converting to lowercase
       * This allows users to search for countries without needing to type accented characters
       *
       * Examples:
       * - "São Tomé" becomes "sao tome"
       * - "México" becomes "mexico"
       * - "Côte d'Ivoire" becomes "cote d'ivoire"
       *
       * @param {string} str - The string to normalize
       * @returns {string} - Normalized string without accents in lowercase
       */
      normalizeString (str) {
        return str
          .normalize('NFD') // Decompose accented characters (é becomes e + ´)
          .replace(/\p{Diacritic}/gu, '') // Remove all diacritical marks (accents, tildes, etc.)
          .toLowerCase() // Convert to lowercase for case-insensitive comparison
      }

      filterCountries () {
        const searchValue = this.normalizeString(this.elements.search.value)
        const popularCountries = this.querySelector('.popular-countries')
        const allCountries = this.querySelectorAll('a')
        let visibleCountries = allCountries.length

        this.elements.resetButton.classList.toggle('hidden', !searchValue)

        if (popularCountries) {
          popularCountries.classList.toggle('hidden', searchValue)
        }

        allCountries.forEach((item) => {
          const countryName = this.normalizeString(item.querySelector('.country').textContent)
          if (countryName.indexOf(searchValue) > -1) {
            item.parentElement.classList.remove('hidden')
            visibleCountries++
          } else {
            item.parentElement.classList.add('hidden')
            visibleCountries--
          }
        })

        if (this.elements.liveRegion) {
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.countrySelectorSearchCount.replace(
            '[count]',
            visibleCountries
          )
        }

        this.querySelector('.country-selector').scrollTop = 0
        this.querySelector('.country-selector__list').scrollTop = 0
      }

      resetFilter (event) {
        event.stopPropagation()
        this.elements.search.value = ''
        this.filterCountries()
        this.elements.search.focus()
      }

      onSearchFocus () {
        this.elements.searchIcon.classList.add('country-filter__search-icon--hidden')
      }

      onSearchBlur () {
        if (!this.elements.search.value) {
          this.elements.searchIcon.classList.remove('country-filter__search-icon--hidden')
        }
      }

      onSearchKeyDown (event) {
        if (event.code.toUpperCase() === 'ENTER') {
          event.preventDefault()
        }
      }
    }
  )
}
